import { z } from 'zod';
import {
  COMPONENT_MAP,
  LAYOUT_SURFACE_LABELS,
  defaultProps,
  requiredTypes,
  type LayoutSurface,
} from './registry.js';

export const LAYOUT_VERSION = 1;
export const MAX_LAYOUT_NODES = 200;
export const MAX_LAYOUT_DEPTH = 6;

/** Node ids are short and URL-safe; the builder generates them, the API only checks them. */
export const nodeIdSchema = z.string().regex(/^[a-zA-Z0-9_-]{4,32}$/);

export interface LayoutNode {
  id: string;
  type: string;
  props: Record<string, unknown>;
  children?: LayoutNode[];
}

/** Structural shape only; `validateLayout` checks types and props against the registry. */
export const layoutNodeSchema: z.ZodType<LayoutNode> = z.lazy(() =>
  z.object({
    id: nodeIdSchema,
    type: z.string().min(1).max(40),
    props: z.record(z.string(), z.unknown()).default({}),
    children: z.array(layoutNodeSchema).optional(),
  }),
) as z.ZodType<LayoutNode>;

export const layoutSchema = z.object({
  version: z.literal(LAYOUT_VERSION),
  nodes: z.array(layoutNodeSchema).max(MAX_LAYOUT_NODES),
});
export type Layout = z.infer<typeof layoutSchema>;

export const EMPTY_LAYOUT: Layout = { version: LAYOUT_VERSION, nodes: [] };

export interface LayoutIssue {
  /** The node id, or null for layout-wide problems. */
  nodeId: string | null;
  path: string;
  message: string;
}

/**
 * Check a layout against the registry: known types only, valid props (normalised with their
 * defaults), containers hold children and leaves don't, parent/child rules, size limits and
 * unique ids. Returns the normalised layout or the issues. The API and the builder both use
 * this, so what the builder accepts the API accepts.
 */
export function validateLayout(
  input: unknown,
  surface?: LayoutSurface,
): { ok: true; layout: Layout } | { ok: false; issues: LayoutIssue[] } {
  const parsed = layoutSchema.safeParse(input);
  if (!parsed.success) {
    return {
      ok: false,
      issues: parsed.error.issues.map((i) => ({
        nodeId: null,
        path: i.path.join('.'),
        message: i.message,
      })),
    };
  }
  const issues: LayoutIssue[] = [];
  const seen = new Set<string>();
  let count = 0;
  const walk = (
    nodes: LayoutNode[],
    parent: LayoutNode | null,
    depth: number,
    path: string,
  ): LayoutNode[] =>
    nodes.map((node, i) => {
      const here = `${path}.${i}`;
      count++;
      if (count > MAX_LAYOUT_NODES)
        issues.push({
          nodeId: node.id,
          path: here,
          message: `A layout holds at most ${MAX_LAYOUT_NODES} components.`,
        });
      if (depth > MAX_LAYOUT_DEPTH)
        issues.push({
          nodeId: node.id,
          path: here,
          message: `Components nest at most ${MAX_LAYOUT_DEPTH} deep.`,
        });
      if (seen.has(node.id))
        issues.push({ nodeId: node.id, path: here, message: `Duplicate component id ${node.id}.` });
      seen.add(node.id);
      const def = COMPONENT_MAP.get(node.type);
      if (!def) {
        issues.push({ nodeId: node.id, path: here, message: `Unknown component "${node.type}".` });
        return node;
      }
      if (def.parentTypes && (!parent || !def.parentTypes.includes(parent.type)))
        issues.push({
          nodeId: node.id,
          path: here,
          message: `${def.label} can only sit inside ${def.parentTypes.join(', ')}.`,
        });
      if (parent) {
        const pdef = COMPONENT_MAP.get(parent.type);
        if (pdef?.childTypes && !pdef.childTypes.includes(node.type))
          issues.push({
            nodeId: node.id,
            path: here,
            message: `${pdef.label} only holds ${pdef.childTypes.join(', ')}.`,
          });
      }
      const props = def.props.safeParse(node.props ?? {});
      if (!props.success) {
        for (const issue of props.error.issues)
          issues.push({
            nodeId: node.id,
            path: `${here}.props.${issue.path.join('.')}`,
            message: issue.message,
          });
      }
      let children: LayoutNode[] | undefined;
      if (def.container) children = walk(node.children ?? [], node, depth + 1, `${here}.children`);
      else if (node.children?.length)
        issues.push({
          nodeId: node.id,
          path: here,
          message: `${def.label} can’t hold other components.`,
        });
      return {
        id: node.id,
        type: node.type,
        props: (props.success ? props.data : node.props) as Record<string, unknown>,
        ...(def.container ? { children: children ?? [] } : {}),
      };
    });
  const nodes = walk(parsed.data.nodes, null, 1, 'nodes');
  if (surface) issues.push(...surfaceIssues({ version: LAYOUT_VERSION, nodes }, surface));
  if (issues.length) return { ok: false, issues };
  return { ok: true, layout: { version: LAYOUT_VERSION, nodes } };
}

/** Every node in document order, with its parent id and depth. */
export function flattenLayout(
  layout: Layout,
): { node: LayoutNode; parentId: string | null; depth: number }[] {
  const out: { node: LayoutNode; parentId: string | null; depth: number }[] = [];
  const walk = (nodes: LayoutNode[], parentId: string | null, depth: number) => {
    for (const node of nodes) {
      out.push({ node, parentId, depth });
      if (node.children) walk(node.children, node.id, depth + 1);
    }
  };
  walk(layout.nodes, null, 0);
  return out;
}

export function findNode(layout: Layout, id: string): LayoutNode | undefined {
  return flattenLayout(layout).find((e) => e.node.id === id)?.node;
}

let counter = 0;
/** A fresh node id: random, so ids from different sessions and AI proposals don't collide. */
export function newNodeId(): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < 8; i++) s += alphabet[Math.floor(Math.random() * alphabet.length)];
  counter = (counter + 1) % 1000;
  return `n${s}${counter.toString(36)}`;
}

/** A new node of `type` with default props (and empty children for containers). */
export function createNode(type: string, props: Record<string, unknown> = {}): LayoutNode {
  const def = COMPONENT_MAP.get(type);
  if (!def) throw new Error(`Unknown component type ${type}`);
  const node: LayoutNode = { id: newNodeId(), type, props: { ...defaultProps(type), ...props } };
  if (def.container) {
    node.children =
      type === 'columns'
        ? Array.from({ length: Number((node.props as { count?: number }).count ?? 2) }, () =>
            createNode('column'),
          )
        : [];
  }
  return node;
}

/** The same tree with fresh ids everywhere (duplicating a node or pasting a template). */
export function cloneNode(node: LayoutNode): LayoutNode {
  return {
    id: newNodeId(),
    type: node.type,
    props: JSON.parse(JSON.stringify(node.props)) as Record<string, unknown>,
    ...(node.children ? { children: node.children.map(cloneNode) } : {}),
  };
}

/** Whether a node's own visibility hides it from anyone (a customer, or a phone). */
function hidesSomething(node: LayoutNode): boolean {
  const v = (node.props as { visibility?: { audience?: string; hideOnMobile?: boolean } })
    .visibility;
  return Boolean(v && ((v.audience && v.audience !== 'all') || v.hideOnMobile));
}

/**
 * The per-surface rules: only components allowed there, required ones present exactly once
 * and not inside anything that hides them, unique ones at most once.
 */
export function surfaceIssues(layout: Layout, surface: LayoutSurface): LayoutIssue[] {
  const issues: LayoutIssue[] = [];
  const where = LAYOUT_SURFACE_LABELS[surface].toLowerCase();
  const seen = new Map<string, number>();
  const walk = (nodes: LayoutNode[], hiddenBy: LayoutNode | null, path: string) =>
    nodes.forEach((node, i) => {
      const here = `${path}.${i}`;
      const def = COMPONENT_MAP.get(node.type);
      if (!def) return;
      seen.set(node.type, (seen.get(node.type) ?? 0) + 1);
      if (def.surfaces && !def.surfaces.includes(surface))
        issues.push({
          nodeId: node.id,
          path: here,
          message: `${def.label} can’t be used on the ${where}.`,
        });
      if (def.unique && seen.get(node.type) === 2)
        issues.push({ nodeId: node.id, path: here, message: `${def.label} can only appear once.` });
      if (def.required?.includes(surface) && hiddenBy) {
        const by = COMPONENT_MAP.get(hiddenBy.type)?.label ?? hiddenBy.type;
        issues.push({
          nodeId: node.id,
          path: here,
          message: `${def.label} is required, so it can’t sit inside a ${by} that hides it from some viewers.`,
        });
      }
      if (node.children)
        walk(node.children, hiddenBy ?? (hidesSomething(node) ? node : null), `${here}.children`);
    });
  walk(layout.nodes, null, 'nodes');
  for (const type of requiredTypes(surface)) {
    if (!seen.has(type)) {
      const label = COMPONENT_MAP.get(type)?.label ?? type;
      issues.push({
        nodeId: null,
        path: 'nodes',
        message: `The ${where} needs a ${label}; it can be moved but not removed.`,
      });
    }
  }
  return issues;
}

/**
 * What the builder must not let an editor do on `surface`: delete a node whose subtree holds
 * a required component, or duplicate one whose subtree holds a unique component.
 */
export function layoutLocks(
  layout: Layout,
  surface: LayoutSurface,
): { noRemove: Set<string>; noDuplicate: Set<string> } {
  const noRemove = new Set<string>();
  const noDuplicate = new Set<string>();
  const walk = (node: LayoutNode): { required: boolean; unique: boolean } => {
    const def = COMPONENT_MAP.get(node.type);
    let required = Boolean(def?.required?.includes(surface));
    let unique = Boolean(def?.unique);
    for (const child of node.children ?? []) {
      const r = walk(child);
      required ||= r.required;
      unique ||= r.unique;
    }
    if (required) noRemove.add(node.id);
    if (unique) noDuplicate.add(node.id);
    return { required, unique };
  };
  layout.nodes.forEach(walk);
  return { noRemove, noDuplicate };
}
